import { useEffect } from "react";
import { Link } from 'react-router-dom';
import { firestore } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const quickLinks = [
  { label: "Homepage", href: "/" },
  { label: "About Us", href: "/our-story" },
  { label: "Our Products", href: "/3d-night-lamp" },
  { label: "Contact us", href: "/faq" },
];

const getSupport = [
  { label: "My Account", href: "/login" },
  { label: "Order History", href: "/orders" },
  { label: "Shipping Policy", href: "/shipping" },
  { label: "Promotions", href: "/promotions" },
];

const followUs = [
  { label: "Instagram", href: "https://www.instagram.com/merchm.store/", icon: "instagram" },
  { label: "Facebook", href: "https://www.facebook.com/merchmasterslk/", icon: "facebook" },
  { label: "Google", href: "https://share.google/zyUr8u1H3B98mZHJY", icon: "google" },
  { label: "TikTok", href: "https://www.tiktok.com/@merchmasterslk", icon: "tiktok" },
];

const socialIcons = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" role="img" aria-hidden="true" className="w-5 h-5">
      <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm4.5-2a1 1 0 11-2 0 1 1 0 012 0z" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" role="img" aria-hidden="true" className="w-5 h-5">
      <path d="M13 3h4a1 1 0 011 1v3h-3a1 1 0 00-1 1v3h4l-1 4h-3v8h-4v-8H8v-4h3V8a5 5 0 015-5z" />
    </svg>
  ),
  google: (
    <svg viewBox="0 0 24 24" fill="currentColor" role="img" aria-hidden="true" className="w-5 h-5">
      <path d="M21.6 12.222c0-.78-.07-1.536-.204-2.222H12v4.207h5.412a4.812 4.812 0 01-2.097 3.157l-.02.122 3.043 2.353.211.02c1.937-1.786 3.051-4.416 3.051-7.637z" />
      <path d="M12 22c2.838 0 5.212-.94 6.95-2.562l-3.32-2.565c-.924.624-2.12.998-3.63.998-2.799 0-5.17-1.889-6.014-4.426H2.57v2.685A9.998 9.998 0 0012 22z" />
      <path d="M5.986 13.445a6.035 6.035 0 010-2.89V7.87H2.57A9.998 9.998 0 002 12c0 1.63.385 3.177 1.07 4.53l3.916-3.085z" />
      <path d="M12 5.48c1.543 0 2.926.521 4.017 1.544l3.01-3.01C18.199 2.831 15.837 2 12 2 7.998 2 4.58 4.291 2.57 7.87l3.416 2.685C6.83 7.906 9.201 5.48 12 5.48z" />
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" fill="currentColor" role="img" aria-hidden="true" className="w-5 h-5">
      <path d="M16.5 3A4.5 4.5 0 0021 7.5v1.034a7.96 7.96 0 01-3.5-.84V14.8a5.7 5.7 0 11-5.7-5.7h.3v2.989a2.711 2.711 0 102.4 2.676V2h2z" />
    </svg>
  ),
};

const Footer = () => {
  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const cached = localStorage.getItem('contactInfoCache');
        if (cached) {
          const { timestamp } = JSON.parse(cached);
          const oneHour = 60 * 60 * 1000;
          if (Date.now() - timestamp < oneHour) {
            return;
          }
        }

        const docRef = doc(firestore, 'WebInfo', 'settings');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().contactInfo) {
          localStorage.setItem('contactInfoCache', JSON.stringify({
            info: docSnap.data().contactInfo,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error('Error loading contact info:', error);
      }
    };

    loadContactInfo();
  }, []);

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand Column */}
          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                src="/logo.png"
                alt="Merch Masters LK logo"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
              />
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-gray-900">Merch Masters LK</h3>
            </div>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              A maker-first studio specializing in illuminated memories, custom 3D-printed lithophane lamps, and personalized gifts. Born in Sri Lanka with a passion for bringing your cherished moments to life.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif font-bold text-gray-900 mb-4 sm:mb-6 text-base sm:text-lg">Quick Links</h4>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-[#E85D44] transition-colors duration-200 text-sm sm:text-base"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get Support */}
          <div>
            <h4 className="font-serif font-bold text-gray-900 mb-4 sm:mb-6 text-base sm:text-lg">Get Support</h4>
            <ul className="space-y-2 sm:space-y-3">
              {getSupport.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-[#E85D44] transition-colors duration-200 text-sm sm:text-base"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us */}
          <div>
            <h4 className="font-serif font-bold text-gray-900 mb-4 sm:mb-6 text-base sm:text-lg">Follow Us</h4>
            <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600">
              {followUs.map(({ label, href, icon }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-[#E85D44] transition-colors duration-200"
                  >
                    <span className="text-[#E85D44]">
                      {socialIcons[icon]}
                    </span>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Location Map */}
        <div className="mt-12">
          <div className="w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <iframe
              title="Merch Masters LK location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.6987349326114!2d79.9641524!3d6.9265688999999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x66533c5d6594e5b1%3A0x3732db85ba0206b5!2sMerch%20Masters%20LK!5e0!3m2!1sen!2slk!4v1760977507885!5m2!1sen!2slk"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-64 sm:h-80 lg:h-96"
              style={{ border: 0 }}
            ></iframe>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4">
          <p className="text-xs sm:text-sm text-gray-500">
            (c) 2021 — All rights reserved.
          </p>
          <div className="flex gap-2 sm:gap-4">
            {/* Payment icons */}
            <img src="/images/payment-stripe.png" alt="Stripe" className="h-5 sm:h-6" onError={(e) => e.target.style.display = 'none'} />
            <img src="/images/payment-paypal.png" alt="PayPal" className="h-5 sm:h-6" onError={(e) => e.target.style.display = 'none'} />
            <img src="/images/payment-visa.png" alt="Visa" className="h-5 sm:h-6" onError={(e) => e.target.style.display = 'none'} />
            <img src="/images/payment-mastercard.png" alt="Mastercard" className="h-5 sm:h-6" onError={(e) => e.target.style.display = 'none'} />
            <img src="/images/payment-maestro.png" alt="Maestro" className="h-5 sm:h-6" onError={(e) => e.target.style.display = 'none'} />
            <img src="/images/payment-discover.png" alt="Discover" className="h-5 sm:h-6" onError={(e) => e.target.style.display = 'none'} />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
